/**
 * @graphql cc
 */
import { useMemo, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useLoomScriptReadTimeContext } from '@confluence/experiment-loom-recording-suggestions-on-confluence-pages-using-llms/entry-points/LoomScriptReadTimeContext';

import { calculateEstimatedReadTime, getAdf, getWordCount } from '../readTimeUtils';
import { extractContentFeatures, extractContentFeaturesSkipExpand } from '../featuresExtractor';
import { ReadTimeQuery } from '../ReadTimeQuery.graphql';

export const useReadTime = (
	contentId: string,
	skipQuery?: boolean,
	isLoomAIScriptExperimentEnabled: boolean | undefined = false,
) => {
	// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations, graphql-client-usage/graphql-generic-types -- Preloaded GraphQL Operation
	const { data, loading, error } = useQuery(ReadTimeQuery, {
		variables: {
			contentId,
			includeMetadata: isLoomAIScriptExperimentEnabled,
		},
		// cache-first: check cache first, if have full data, return directly; if not, make a network request
		// cache-only: only read from cache, never make a network request
		fetchPolicy: isLoomAIScriptExperimentEnabled ? 'cache-first' : 'cache-only',
		skip: skipQuery,
	});

	const { adf, readTime, wordCount, features, featuresSkipExpand } = useMemo(() => {
		const adfFromData = getAdf(data);

		if (!adfFromData || loading || error || !contentId) {
			return {
				readTime: null,
				wordCount: null,
				features: null,
				featuresSkipExpand: null,
			};
		}

		return {
			adf: adfFromData,
			readTime: calculateEstimatedReadTime(adfFromData),
			wordCount: getWordCount(adfFromData),
			features: extractContentFeatures(adfFromData),
			featuresSkipExpand: extractContentFeaturesSkipExpand(adfFromData),
		};
	}, [loading, error, data, contentId]);

	const contentNode = data?.content?.nodes?.[0];
	const contentType = contentNode?.type;

	const { updateLoomScriptReadTimeEligibilityForPage } = useLoomScriptReadTimeContext();

	useEffect(() => {
		if (isLoomAIScriptExperimentEnabled) {
			updateLoomScriptReadTimeEligibilityForPage(
				contentNode,
				readTime,
				features?.videos && features.videos.length > 0,
			);
		}
	}, [
		contentNode,
		isLoomAIScriptExperimentEnabled,
		readTime,
		features,
		updateLoomScriptReadTimeEligibilityForPage,
	]);

	return {
		loading,
		error,
		adf,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
	};
};
